/**
 * AB#1904 (W-1) — Stripe Checkout return: cancelled page.
 *
 * Route: `/subscription/cancelled`
 *
 * Per Dina §C — reassuring tone, no guilt language. 4-chip optional survey
 * fires `subscription.cancelReason` via trackEvent.
 *
 * Unauth tolerance: like SuccessPage, we redirect to login with returnTo
 * if there's no token — the owner may have bounced back from Stripe with
 * an expired JWT.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useCurrentSubscription } from '../../../hooks/useCurrentSubscription';
import { useToast } from '../../../contexts/ToastContext';
import { trackEvent } from '../../../lib/analytics';
import s from './CancelledPage.module.css';

type CancelReason = 'price' | 'thinking' | 'features' | 'other';

const REASONS: { key: CancelReason; labelKey: string }[] = [
    { key: 'price', labelKey: 'subscription.cancelled.reasonPrice' },
    { key: 'thinking', labelKey: 'subscription.cancelled.reasonThinking' },
    { key: 'features', labelKey: 'subscription.cancelled.reasonFeatures' },
    { key: 'other', labelKey: 'subscription.cancelled.reasonOther' },
];

const CancelledPage: React.FC = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthContext();
    const { data: subscription } = useCurrentSubscription();
    const { showToast } = useToast();
    const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            const back = encodeURIComponent('/subscription/cancelled');
            navigate(`/form/login?returnTo=${back}`, { replace: true });
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        trackEvent('subscription.upgradeCancelled', {
            fromPlan: subscription?.planName ?? '',
            intendedPlan: '',
        });
        // We intentionally only want to fire this once per page mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isAuthenticated) return null;

    const handleReason = (r: CancelReason) => {
        if (selectedReason) return;
        setSelectedReason(r);
        trackEvent('subscription.cancelReason', { reason: r });
        showToast(t('subscription.cancelled.surveyThanks'), 'success');
    };

    const planName = subscription?.planName ?? '';

    return (
        <div className={s.page}>
            <div className={s.card} role="status">
                <div className={s.iconCircle} aria-hidden="true">↩</div>
                <h1 className={s.title}>{t('subscription.cancelled.title')}</h1>
                <p className={s.sub}>
                    {t('subscription.cancelled.sub').replace('{planName}', planName)}
                </p>

                <div className={s.actions}>
                    <button
                        type="button"
                        className={s.btnPrimary}
                        onClick={() => navigate('/subscription')}
                    >
                        {t('subscription.cancelled.ctaBack')}
                    </button>
                    <button
                        type="button"
                        className={s.btnGhost}
                        onClick={() => navigate('/dashboard')}
                    >
                        {t('subscription.cancelled.ctaDashboard')}
                    </button>
                </div>

                <div
                    className={s.surveyRow}
                    role="radiogroup"
                    aria-label={t('subscription.cancelled.surveyCaption')}
                >
                    <p className={s.surveyCaption}>
                        {t('subscription.cancelled.surveyCaption')}
                    </p>
                    <div className={s.chips}>
                        {REASONS.map(r => {
                            const checked = selectedReason === r.key;
                            return (
                                <button
                                    key={r.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={checked}
                                    disabled={!!selectedReason && !checked}
                                    className={`${s.chip} ${checked ? s.chipChecked : ''}`}
                                    onClick={() => handleReason(r.key)}
                                >
                                    {t(r.labelKey)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CancelledPage;
